import { createAsyncThunk, isPending } from '@reduxjs/toolkit';
import { apiOkr } from 'app/config/axios-interceptor';
import { EntityState, createEntitySlice, serializeAxiosError } from 'app/shared/reducers/reducer.utils';

const initialState: EntityState<any> = {
  loading: false,
  errorMessage: null,
  entities: null,
  entity: {},
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/okr/checkin-draft-year';

export const getListKrCheckInDraft = createAsyncThunk(
  'checkIn/fetch_list_Kr_draft_year',
  async (krId: string, { rejectWithValue }) => {
    try {
      const response = await apiOkr.get<any>(`${apiUrl}/next-checkin-draft-item/${krId}`);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  { serializeError: serializeAxiosError },
);

export const getKrQualCheckInDraftYear = createAsyncThunk(
  'checkIn/fetch_KrQual_draft_year',
  async (krId: string, { rejectWithValue }) => {
    try {
      const response = await apiOkr.get<any>(`${apiUrl}/kr-qualitative/${krId}`);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  { serializeError: serializeAxiosError },
);

export const getKrQuantiAccumulateCheckInDraftYear = createAsyncThunk(
  'checkIn/fetch_KrQuanti_accumulate_draft_year',
  async (krId: string, { rejectWithValue }) => {
    try {
      const response = await apiOkr.get<any>(`${apiUrl}/kr-quantitative-accumulate/${krId}`);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  { serializeError: serializeAxiosError },
);

export const getKrQuantiManualCheckInDraftYear = createAsyncThunk(
  'checkIn/fetch_KrQuanti_manual_draft_year',
  async (krId: string, { rejectWithValue }) => {
    try {
      const response = await apiOkr.get<any>(`${apiUrl}/kr-quantitative-manual/${krId}`);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  { serializeError: serializeAxiosError },
);

type ICheckInKrQualDraftYear = {
  krId: string;
  workStatus: number;
  completedOn?: Date;
  completeNote?: string;
  expectedCompleteOn?: Date;
  unFinishNote?: string;
  progressionPoint?: number;
  qualityPoint?: number;
  ratingNote?: string;
  issue?: string;
  solution?: string;
  note?: string;
  sendNotification: boolean;
};

export const checkIntKrQualDraftYear = createAsyncThunk(
  'checkIn/create_checkin_KrQual_draft_year',
  async (payload: ICheckInKrQualDraftYear, { rejectWithValue }) => {
    try {
      const response = await apiOkr.post<any>(`${apiUrl}/kr-qualitative`, payload);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  { serializeError: serializeAxiosError },
);

type ICheckInKrQuantiAccumulateDraftYear = {
  krId: string;
  firstTenYearResult?: number;
  novResult?: number;
  decResult?: number;
  workStatus?: number;
  unfinishNote?: string;
  issue?: string;
  solution?: string;
  note?: string;
  sendNotification: boolean;
};

export const checkIntKrQuantiAccumulateDraftYear = createAsyncThunk(
  'checkIn/create_checkin_KrQuanti_accumulate_draft_year',
  async (payload: ICheckInKrQuantiAccumulateDraftYear, { rejectWithValue }) => {
    try {
      const response = await apiOkr.post<any>(`${apiUrl}/kr-quantitative-accumulate`, payload);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  { serializeError: serializeAxiosError },
);

type ICheckInKrQuantiManualDraftYear = {
  krId: string;
  wholeYearResult?: number;
  finalPoint?: number;
  workStatus?: number;
  unfinishNote?: string;
  issue?: string;
  solution?: string;
  note?: string;
  sendNotification: boolean;
};

export const checkIntKrQuantiManualDraftYear = createAsyncThunk(
  'checkIn/create_checkin_KrQuanti_manual_draft_year',
  async (payload: ICheckInKrQuantiManualDraftYear, { rejectWithValue }) => {
    try {
      const response = await apiOkr.post<any>(`${apiUrl}/kr-quantitative-manual`, payload);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  { serializeError: serializeAxiosError },
);

export const getKrConfirmComplete = createAsyncThunk(
  'checkIn/fetch_list_kr_confirm_complete',
  ({ partyId }: any, { rejectWithValue }) => {
    const response = apiOkr
      .get<any>(`${apiUrl}/draft-year-submitted-info`, {
        params: {
          partyId,
        },
      })
      .catch(err => {
        return rejectWithValue(err);
      });
    return response;
  },
  { serializeError: serializeAxiosError },
);

// slice

export const CheckInDraftSlice = createEntitySlice({
  name: 'checkInDraft',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getListKrCheckInDraft.fulfilled, (state, action) => {
        state.loading = false;
        state.entities = action.payload.data;
      })
      .addCase(getKrQualCheckInDraftYear.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getKrQuantiAccumulateCheckInDraftYear.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getKrQuantiManualCheckInDraftYear.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(checkIntKrQualDraftYear.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
      })
      .addCase(checkIntKrQuantiAccumulateDraftYear.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
      })
      .addCase(checkIntKrQuantiManualDraftYear.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
      })
      .addMatcher(isPending(getListKrCheckInDraft), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.entities = [];
      })
      .addMatcher(isPending(checkIntKrQualDraftYear, checkIntKrQuantiManualDraftYear, checkIntKrQuantiAccumulateDraftYear), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      })
      .addMatcher(isPending(getKrQualCheckInDraftYear, getKrQuantiAccumulateCheckInDraftYear, getKrQuantiManualCheckInDraftYear), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
        state.entity = {};
      });
  },
});

export const { reset } = CheckInDraftSlice.actions;

// Reducer
export default CheckInDraftSlice.reducer;
